import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SdmUtilService } from './sdm-util.service';

export interface ContentFactory {
	substitute(contentValue: string, ...substitutionArguments: any[]): string;
	clearCache(): void;
	initAll(): Promise<void>;
	setContent(contentId: string, newVal: string): void;
	getContentPromise(contentId: string): Promise<string>;
	getContent(
		contentId: string,
		arg1?: any, arg2?: any, arg3?: any, arg4?: any, arg5?: any,
		arg6?: any, arg7?: any, arg8?: any, arg9?: any
	): string;
	getRequestedContent(): { contentId: string; contentValue: string }[];
	clearAllAndReload(): void;
}

@Injectable({ providedIn: 'root' })
export class ContentService implements ContentFactory {
	private content: Record<string, any> = {};
	private contentPromises: Record<string, any> = {};
	private requestedContent: Record<string, { value: string; counter: number }> = {};
	private counter = 0;
	private $localStorage: any;
	private $window: any;
	private http = inject(HttpClient);
	private sdmUtil = inject(SdmUtilService);
	private contentReady = false;

	constructor(
		
	) {
		this.sdmUtil.isAuthenticated.then(() => {
			this.initAll();
		});
	}


	private getLocalStorage() {
		if (!this.$localStorage) {
			// Use browser's native localStorage instead of AngularJS $localStorage
			this.$localStorage = {
				get: (key: string) => {
					const value = localStorage.getItem(key);
					return value ? JSON.parse(value) : undefined;
				},
				set: (key: string, value: any) => {
					localStorage.setItem(key, JSON.stringify(value));
				},
				remove: (key: string) => {
					localStorage.removeItem(key);
				}
			};
		}
		return this.$localStorage;

		// Native localStorage replacement for AngularJS $localStorage
		// if (!this.$localStorage) {
		// 	const ngInjector = (window as any).angular.element(document.body).injector?.();
		// 	if (!ngInjector) {
		// 		throw new Error('AngularJS injector not available yet. Make sure this service is used after bootstrap.');
		// 	}
		// 	this.$localStorage = ngInjector.get('$localStorage');
		// }
		// return this.$localStorage;
	}

	private getWindow() {
		if (!this.$window) {
			// Use browser's native window instead of AngularJS $window
			this.$window = window;
		}
		return this.$window;
	}

	private requestedContentPush(contentId: string, value: string) {
		this.requestedContent[contentId] = { value, counter: this.counter++ };
	}

	substitute(contentValue: string, ...substitutions: any[]): string {
		for (let i = 0; i < substitutions.length; i++) {
			contentValue = contentValue.replace(`$${i + 1}`, substitutions[i]);
		}
		return contentValue;
	}

	clearCache(): void {
		this.content = {};
		this.contentPromises = {};
		const localStorage = this.getLocalStorage();
		delete localStorage.contentSaveTS;
		delete localStorage.content;
	}

	private getAll(): Promise<any> {
		return firstValueFrom(this.http.get('/api/Content/GetAll'));
	}

	private getSectionFromContentId(contentId: string): string {
		return contentId.substring(0, contentId.indexOf('.'));
	}

	private getContentBySection(section: string): Promise<any> {
		return firstValueFrom(this.http.get(`/api/Content/GetSection/${section}`));
	}

	private getContentById(contentId: string): Promise<any> {
		const content = this.http.get(`/api/Content/GetContent/${contentId}`)
		const res =  firstValueFrom(content);
		return res;
	}

	private addMinutes(date: Date, minutes: number): Date {
		return new Date(date.getTime() + minutes * 60000);
	}

	async initAll(): Promise<void> {
		const localStorage = this.getLocalStorage();
		let mustLoad = true;

		if (!localStorage.content) {
			delete localStorage.contentSaveTS;
		}

		const cachedContent = localStorage.content;
		if (cachedContent) {
			this.content = cachedContent;
		}

		const lastSaved = localStorage.contentSaveTS;
		if (lastSaved && typeof lastSaved === 'number') {
			const freshUntil = this.addMinutes(new Date(lastSaved), 60);
			const now = new Date();
			if (now < freshUntil) {
				mustLoad = false;
			}
		}

		if (mustLoad) {
			const response: any = await this.getAll();
			this.content = response;
			this.contentPromises = {};
			localStorage.content = this.content;
			localStorage.contentSaveTS = new Date().getTime();
		}
		
		this.contentReady = true;
	}

	isContentReady(): boolean {
		return this.contentReady;
	}


	setContent(contentId: string, newVal: string): void {
		firstValueFrom(this.http.post(`api/Content/PostContent/${contentId}`, { contentValue: newVal }))
			.then(() => {
				const section = this.getSectionFromContentId(contentId);
				this.content[section] = this.content[section] || {};
				this.content[section][contentId] = newVal;

				this.contentPromises[section] = this.contentPromises[section] || { subPromises: {} };
				this.contentPromises[section].subPromises[contentId] = Promise.resolve(newVal);
			});
	}

	getContentPromise(contentId: string): Promise<string> {
		if (!contentId) throw 'ContentId cannot be null.';
		const index = contentId.indexOf('.');
		const section = this.getSectionFromContentId(contentId);
		if (index === -1 || index === contentId.length) {
			throw `ContentId must be on the form x.y. Found this contentId: ${contentId}`;
		}

		if (this.content[section] && this.content[section][contentId]) {
			const val = this.content[section][contentId];
			this.requestedContentPush(contentId, val);
			return Promise.resolve(val);
		}

		if (!this.contentPromises[section]) {
			this.contentPromises[section] = { sectionPromise: null, subPromises: {} };
			this.content[section] = {};

			this.contentPromises[section].sectionPromise = this.getContentBySection(section).then((response: any) => {
				Object.assign(this.content[section], response);
				return this.content[section];
			});
		}

		if (!this.contentPromises[section].subPromises[contentId]) {
			this.contentPromises[section].subPromises[contentId] = this.contentPromises[section].sectionPromise.then(
				(contentSection: any) => {
					const val = contentSection[contentId];
					if (val) {
						return val;
					} else {
						return this.getContentById(contentId).then((response: any) => {
							contentSection[contentId] = response;
							return response;
						});
					}
				}
			);
		}

		this.contentPromises[section].subPromises[contentId].then(value => {
			this.requestedContentPush(contentId, value);
		});

		return this.contentPromises[section].subPromises[contentId];
	}

	getContent(
		contentId: string,
		arg1?: any, arg2?: any, arg3?: any, arg4?: any, arg5?: any,
		arg6?: any, arg7?: any, arg8?: any, arg9?: any
	): string {
		this.getContentPromise(contentId);
		const section = this.getSectionFromContentId(contentId);
		const rawValue = this.content[section]?.[contentId];

		if (rawValue) {
			this.requestedContentPush(contentId, rawValue);
			return this.substitute(rawValue, arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9);
		}

		const fallback = contentId.includes('.')
			? this.camelCaseToSentence(contentId.split('.').pop() || '')
			: contentId;

		const val = fallback ? fallback.charAt(0).toUpperCase() + fallback.slice(1) : contentId;
		this.requestedContentPush(contentId, val);
		return val;
	}

	getRequestedContent(): { contentId: string; contentValue: string }[] {
		return Object.entries(this.requestedContent)
			.sort(([, a], [, b]) => b.counter - a.counter)
			.slice(0, 200)
			.map(([contentId, { value }]) => ({ contentId, contentValue: value }));
	}

	clearAllAndReload(): void {
		this.clearCache();
		this.initAll().then(() => {
			const win = this.getWindow();
			win.location.reload();
		});
	}

	private camelCaseToSentence(str: string): string {
		// Handle special cases first
		return str
			// Add space after + and other special characters
			.replace(/([+&@#$%])/g, '$1 ')
			// Add space before uppercase letter that follows lowercase or number
			.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
			// Add space before lowercase letter that follows multiple uppercase letters
			// This handles cases like "IVCtemplates" -> "IVC templates"
			.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
			// Clean up multiple spaces
			.replace(/\s+/g, ' ')
			.trim();
	}

}
